<template>
  <div class="section-list">
    <div v-if="loading" class="loading">加载中...</div>
    <div v-else class="layout">
      <!-- 顶部工具栏：左侧标签 + 右侧返回按钮 -->
      <div class="list-toolbar">
        <span class="list-tag">{{ title }}</span>
        <button v-if="currentItem" class="btn-back" @click="clearSelection">返回列表</button>
      </div>
      <!-- 初始：标题列表占满整行 -->
      <template v-if="!currentItem">
        <ul class="title-list">
          <li v-for="(item, idx) in paginatedItems" :key="item.id"
            :class="['title-item', selectedId === item.id ? 'active' : '']" @click="selectItem(item)">
            <span class="item-title">{{ (currentPage - 1) * pageSize + idx + 1 + '.' + (item.title || '标题') }}</span>
            <span class="upload-time">{{ formatDate(item.created_at) }}</span>
          </li>
        </ul>
        <!-- 分页控件 -->
        <div class="pagination">
          <span class="pagination-total">共 {{ items.length }} 条</span>
          <select v-model.number="pageSize" @change="onPageSizeChange" class="page-size-select">
            <option :value="10">10条/页</option>
            <option :value="20">20条/页</option>
            <option :value="50">50条/页</option>
          </select>
          <button class="page-btn" :disabled="currentPage === 1" @click="goToPrevPage">‹</button>
          <span class="page-current">{{ currentPage }}</span>
          <button class="page-btn" :disabled="currentPage >= totalPages" @click="goToNextPage">›</button>
        </div>
      </template>

      <!-- 点击后：显示富文本内容或图片，占满整个页面 -->
      <div class="detail detail-full" v-else>
        <div v-if="contentHtml(currentItem)" class="detail-html" v-html="contentHtml(currentItem)"></div>
        <img v-else-if="imageSrc(currentItem)" :src="imageSrc(currentItem)" alt="详情图片"
          class="detail-image detail-image-full" />
        <div v-else class="placeholder">暂无内容</div>
      </div>
    </div>
    <div v-if="error" class="error">{{ error }}</div>
  </div>
</template>

<script>
export default {
  name: 'SectionList',
  props: {
    sectionKey: { type: String, required: true },
    title: { type: String, default: '考试文件' },
    resetToken: { type: Number, default: 0 }
  },
  data() {
    return {
      items: [],
      loading: false,
      error: '',
      selectedId: null,
      currentPage: 1,
      pageSize: 10
    };
  },
  watch: {
    sectionKey: {
      immediate: true,
      handler() {
        this.fetchItems();
      }
    },
    // 外部触发的重置（用于左侧 li 再次点击时返回列表）
    resetToken() {
      this.clearSelection()
      this.currentPage = 1 // 重置时回到第一页
    }
  },
  methods: {
    async fetchItems() {
      this.loading = true;
      this.error = '';
      try {
        const resp = await fetch(`/api/admin/section/public/?section=${encodeURIComponent(this.sectionKey)}`);
        if (!resp.ok) {
          throw new Error(`HTTP ${resp.status}`)
        }
        let data
        try {
          data = await resp.json();
        } catch (e) {
          throw new Error('解析响应数据失败')
        }
        const list = Array.isArray(data) ? data : (data?.results || []);
        // 按发布时间升序排序（越早发布序号越小）
        this.items = [...list].sort((a, b) => {
          const ta = new Date(a?.created_at || 0).getTime();
          const tb = new Date(b?.created_at || 0).getTime();
          if (Number.isNaN(ta) && Number.isNaN(tb)) {
            return (a?.id || 0) - (b?.id || 0);
          }
          if (Number.isNaN(ta)) return -1;
          if (Number.isNaN(tb)) return 1;
          if (ta === tb) return (a?.id || 0) - (b?.id || 0);
          return ta - tb;
        });
        // 不默认选中任何项，需点击标题后才显示图片
        this.selectedId = null;
        // 重置到第一页
        this.currentPage = 1;
      } catch (e) {
        console.error('获取列表失败', e);
        this.error = '获取列表失败，请稍后重试';
      } finally {
        this.loading = false;
      }
    },
    resolveUrl(path) {
      if (!path) return '';
      if (/^https?:\/\//.test(path)) return path;
      return path.startsWith('/') ? path : `/${path}`;
    },
    imageSrc(item) {
      const raw = item.image_url || item.image;
      return this.resolveUrl(raw);
    },
    contentHtml(item) {
      // 后端已使用 bleach 处理富文本，前端直接展示
      const html = item?.content || item?.content_html || '';
      return typeof html === 'string' ? html : '';
    },
    selectItem(item) {
      try {
        const arr = JSON.parse(localStorage.getItem('app_history') || '[]')
        arr.push({ hash: '#home', nav: '考试文件', subNav: this.title })
        localStorage.setItem('app_history', JSON.stringify(arr.slice(-20)))
      } catch (_) { }
      this.selectedId = item?.id || null;
    },
    clearSelection() {
      this.selectedId = null;
    },
    formatDate(d) {
      if (!d) return '-';
      const dt = new Date(d);
      if (isNaN(dt.getTime())) return d;
      const y = dt.getFullYear();
      const m = String(dt.getMonth() + 1).padStart(2, '0');
      const da = String(dt.getDate()).padStart(2, '0');
      return `${y}-${m}-${da}`;
    },
    // 翻页功能
    goToPrevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        // 滚动到顶部
        this.$nextTick(() => {
          const container = this.$el.querySelector('.title-list');
          if (container) {
            container.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        });
      }
    },
    goToNextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        // 滚动到顶部
        this.$nextTick(() => {
          const container = this.$el.querySelector('.title-list');
          if (container) {
            container.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        });
      }
    },
    onPageChange() {
      // 页码选择变化时滚动到顶部
      this.$nextTick(() => {
        const container = this.$el.querySelector('.title-list');
        if (container) {
          container.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    },
    onPageSizeChange() {
      this.currentPage = 1;
      this.$nextTick(() => {
        const container = this.$el.querySelector('.title-list');
        if (container) {
          container.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    }
  },

  computed: {
    currentItem() {
      if (!this.items || !this.items.length) return null;
      return this.items.find(x => x.id === this.selectedId) || null;
    },
    // 总页数
    totalPages() {
      if (!this.items || this.items.length === 0) return 0;
      return Math.ceil(this.items.length / this.pageSize);
    },
    // 当前页显示的数据
    paginatedItems() {
      if (!this.items || this.items.length === 0) return [];
      const start = (this.currentPage - 1) * this.pageSize;
      const end = start + this.pageSize;
      return this.items.slice(start, end);
    }
  },
};
</script>

<style scoped>
.section-list {
  background: transparent;
  border-radius: 0;
  padding: 0;
  box-shadow: none;
  width: 100%;
}

.layout {
  display: block;
}

.title-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.title-item {
  width: 100%;
  padding: 12px 16px;
  cursor: pointer;
  color: var(--color-text-primary);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 10px;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.06);
  transition: all .2s ease;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
}

.title-item:first-child {
  padding-top: 12px;
}

.title-item:hover {
  border-color: var(--color-primary);
  box-shadow: 0 6px 12px rgba(16, 24, 40, 0.08);
  transform: translateY(-1px);
}

.title-item.active {
  border-color: var(--color-primary);
  background: #f0f7ff;
  font-weight: var(--font-weight-bold);
}

.item-title {
  flex: 1;
  margin-right: 12px;
}

.upload-time {
  color: #6b7280;
  font-size: 12px;
  white-space: nowrap;
}

.list-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 6px 0 8px;
}

.list-tag {
  display: inline-block;
  font-size: 12px;
  color: var(--color-secondary);
  background: #eef2ff;
  border: 1px solid #dbeafe;
  border-radius: 8px;
  padding: 4px 10px;
  line-height: 1;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.06);
}

.btn-back {
  appearance: none;
  border: 1px solid var(--color-primary);
  background: var(--color-primary);
  color: #fff;
  padding: 6px 12px;
  border-radius: var(--radius-sm);
  font-size: 13px;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(37, 99, 235, 0.25);
  transition: background-color .15s ease, transform .15s ease;
}

.btn-back:hover {
  background: #0052a3;
  transform: translateY(-1px);
}

.btn-back:active {
  transform: translateY(0);
}

.detail {
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.detail-full {
  width: 100%;
  height: calc(100vh - 140px);
}

.detail-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.detail-image-full {
  width: 100%;
  height: 100%;
}

.detail-html {
  width: 100%;
  height: 100%;
  overflow: auto;
  padding: 16px;
  background: var(--color-surface);
  box-sizing: border-box;
}

.detail-html {
  font-family: var(--font-family-sans);
  font-size: 16px;
  line-height: 1.8;
  color: var(--color-text-primary);
}

.detail-html p {
  margin: 0.6em 0;
}

.detail-html h1 {
  font-size: 1.8em;
  margin: 0.8em 0 0.5em;
  font-weight: 700;
}

.detail-html h2 {
  font-size: 1.6em;
  margin: 0.8em 0 0.5em;
  font-weight: 700;
}

.detail-html h3 {
  font-size: 1.4em;
  margin: 0.8em 0 0.5em;
  font-weight: 700;
}

.detail-html h4,
.detail-html h5,
.detail-html h6 {
  margin: 0.8em 0 0.5em;
  font-weight: 700;
}

.detail-html img {
  max-width: 100%;
  height: auto;
}

.detail-html table {
  width: 100%;
  border-collapse: collapse;
}

.detail-html th,
.detail-html td {
  border: 1px solid var(--color-border);
  padding: 6px;
}

.detail-html ol,
.detail-html ul {
  padding-left: 2em;
  margin: 0.6em 0;
}

.detail-html li {
  margin: 0.4em 0;
}

.detail-html blockquote {
  border-left: 3px solid var(--color-border);
  margin: 0.8em 0;
  padding-left: 1em;
  color: var(--color-text-secondary);
}

.detail-html pre,
.detail-html code {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  background: #f8fafc;
  border-radius: var(--radius-sm);
  padding: 6px;
}

.detail-html a {
  color: #2563eb;
  text-decoration: underline;
}

.detail-html u {
  text-decoration: underline;
}

.detail-html [style*="text-decoration:underline"] {
  text-decoration: underline !important;
}

.detail-html .mce-text-left {
  text-align: left;
}

.detail-html .mce-text-center {
  text-align: center;
}

.detail-html .mce-text-right {
  text-align: right;
}

.detail-html .mce-text-justify {
  text-align: justify;
}

.detail-html .align-left {
  text-align: left;
}

.detail-html .align-center {
  text-align: center;
}

.detail-html .align-right {
  text-align: right;
}

.detail-html .align-justify {
  text-align: justify;
}

.detail-html [style*="text-align:left"] {
  text-align: left !important;
}

.detail-html [style*="text-align:center"] {
  text-align: center !important;
}

.detail-html [style*="text-align:right"] {
  text-align: right !important;
}

.detail-html [style*="text-align:justify"] {
  text-align: justify !important;
}

.detail-html img.align-left,
.detail-html img.alignleft {
  display: block;
  margin-left: 0;
  margin-right: auto;
}

.detail-html img.align-center,
.detail-html img.aligncenter {
  display: block;
  margin-left: auto;
  margin-right: auto;
}

.detail-html img.align-right,
.detail-html img.alignright {
  display: block;
  margin-left: auto;
  margin-right: 0;
}

.placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-primary);
  font-size: 28px;
}

.error {
  margin-top: 8px;
  color: #d93025;
}

/* 分页样式 */
.pagination {
  margin-top: 20px;
  padding: 15px 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}

.page-btn {
  padding: 6px 14px;
  background-color: #f8f9fa;
  color: var(--color-text-primary);
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.2s ease;
}

.page-btn:hover:not(:disabled) {
  background-color: #e9ecef;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination .page-current {
  min-width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #1677ff;
  color: #fff;
  border-radius: 4px;
  font-size: 13px;
}

.page-info {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: var(--color-text-secondary);
}

.page-select {
  padding: 2px 6px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 13px;
  margin: 0 2px;
}

.page-size-select {
  padding: 6px 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 13px;
}

.pagination-total {
  color: var(--color-text-secondary);
  font-size: 13px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .pagination {
    gap: 8px;
  }

  .page-btn {
    padding: 5px 10px;
    font-size: 12px;
  }

  .page-info {
    font-size: 12px;
  }
}

@media (max-width: 768px) {
  .detail-full {
    height: auto;
    min-height: 50vh;
  }
}
</style>
